import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDirectorDashboard } from '../hook/useDirectorDashboard.mjs';
import { getReportStatus, getStageLabel } from '../../utils/statusConfig.mjs';
import PageShell from '../../components/PageShell.mjs';

const RegionalDirectorDashboard = () => {
  const navigate = useNavigate();
  const {
    pendingReports,
    approvedReports,
    sentBackReports,
    loading,
    error,
    stats,
    user,
    fetchPendingReports,
    setError
  } = useDirectorDashboard('regional');

  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  // Get reports based on active tab
  const currentReports = activeTab === 'pending'
    ? pendingReports
    : activeTab === 'sentBack' ? sentBackReports : approvedReports;

  const formatCurrency = (amount) => {
    // parseFloat guard: amounts arrive as DECIMAL strings, which render as NaN.
    const n = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(Number.isFinite(n) ? n : 0);
  };

  const _formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPeriod = (month, year) => {
    const monthName = new Date(0, month - 1).toLocaleString('default', { month: 'long' });
    return `${monthName} ${year}`;
  };

  // Check if regional director can approve this report.
  //
  // The server sends `viewOnly` for a report at this stage that someone else was
  // named to approve - cover while the director is away - and for one submitted
  // with the stage removed. Those are listed rather than hidden, so the director
  // can follow what happened in their region. Approving and sending back happen
  // inside the report, not from this table.
  // Status wording and colour come from the shared vocabulary, which knows who
  // is reading: a report waiting on this director says "Awaiting Your
  // Approval". The private map this replaces covered three statuses and fell
  // through to `label: status`, so anything else printed a raw database token.
  const getStatusBadge = (report) => {
    const meta = getReportStatus(report.status, report, {
      viewerRole: user?.role,
      viewerId: user?.id,
    });
    return { label: meta.label, className: meta.badge, description: meta.description };
  };

  const handleViewReport = (reportId) => {
    navigate(`/monthly-report/${reportId}`);
  };

  const shellProps = {
    // "Regional Director Dashboard" is now the page of cards that leads here.
    title: 'Reports Approval',
    subtitle: (
      <>
        Review and approve reports for your region
        {user?.region && <span className="font-medium whitespace-nowrap"> ({user.region})</span>}
      </>
    ),
  };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </PageShell>
    );
  }

  return (
    <>
      {/* Notification. Outside PageShell: its content column is a stacking
          context (z-index 1), which would put this fixed toast under the navbar. */}
      {notification && (
        <div className={`fixed top-5 right-5 px-5 py-3 rounded-lg text-white font-medium z-[10000] flex items-center justify-between min-w-[300px] shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' :
          notification.type === 'error' ? 'bg-red-500' :
          'bg-blue-500'
        }`}>
          {notification.message}
          <button onClick={() => setNotification(null)} className="ml-3 text-xl font-bold hover:opacity-80">&times;</button>
        </div>
      )}

    <PageShell {...shellProps}>
        {/* Error Display */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span>!</span>
              <span>{error}</span>
            </span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              Dismiss
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6 mb-4 sm:mb-6">
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-blue-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-gray-800">{stats.totalPending}</div>
              <div className="text-sm text-gray-500">Pending Approval</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-gray-800">{formatCurrency(stats.totalAmount)}</div>
              <div className="text-sm text-gray-500">Total Amount</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 min-w-0">
            <div className="shrink-0 w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <div className="text-xl sm:text-3xl font-bold tabular-nums break-words text-green-600">{stats.totalApproved || 0}</div>
              <div className="text-sm text-gray-500">Approved</div>
            </div>
          </div>
        </div>

        {/* Tabs and Refresh */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <div className="grid grid-cols-3 sm:flex gap-2">
            <button
              onClick={() => setActiveTab('pending')}
              className={`min-h-[44px] px-2 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
                activeTab === 'pending'
                  ? 'bg-stanford-red text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
              }`}
            >
              Pending ({stats.totalPending})
            </button>
            <button
              onClick={() => setActiveTab('approved')}
              className={`min-h-[44px] px-2 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
                activeTab === 'approved'
                  ? 'bg-green-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
              }`}
            >
              Approved ({stats.totalApproved || 0})
            </button>
            <button
              onClick={() => setActiveTab('sentBack')}
              className={`min-h-[44px] px-2 sm:px-5 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium leading-tight transition-all ${
                activeTab === 'sentBack'
                  ? 'bg-amber-600 text-white shadow-lg'
                  : 'bg-white text-gray-600 hover:bg-gray-100 shadow'
              }`}
            >
              Sent Back ({stats.totalSentBack || 0})
            </button>
          </div>
          <button
            onClick={fetchPendingReports}
            className="min-h-[44px] sm:min-h-0 justify-center px-4 py-2 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {/* Reports Table */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full responsive-table">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Region</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Period</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Submitted By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentReports.map((report) => {
                  const statusBadge = getStatusBadge(report);

                  return (
                    <tr key={report._id || report.id} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-700" data-cell="title">{report.region || 'N/A'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-label="Period">
                        {report.month && report.year ? formatPeriod(report.month, report.year) : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 font-medium tabular-nums" data-label="Amount">
                        {formatCurrency(report.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700" data-label="Submitted by">
                        {report.preparer
                          ? `${report.preparer.firstName} ${report.preparer.lastName}`
                          : '-'}
                      </td>
                      <td className="px-4 py-3" data-label="Status">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.className}`}>
                          {statusBadge.label}
                        </span>
                        {report.status === 'rejected' && (
                          <div className="mt-1 text-xs text-amber-800 max-w-xs">
                            {getStageLabel(report.rejectionStage)}
                            {report.rejectionReason ? ` · ${report.rejectionReason}` : ''}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3" data-cell="action">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewReport(report._id || report.id)}
                            className="px-3 py-1.5 text-sm text-stanford-red border border-stanford-red rounded hover:bg-stanford-red hover:text-white transition-colors"
                          >
                            View
                          </button>
                          {/* No Approve / Send Back here. Signing off a
                              month's spend from a table row means doing it
                              without having opened the claims; both actions
                              live inside the report. The read-only note stays,
                              because a director whose stage was covered or
                              removed needs to know why the row is inert. */}
                          {report.viewOnly && (
                            <span
                              className="px-3 py-1.5 text-sm rounded font-medium bg-sky-100 text-sky-800"
                              title={report.coveredBy
                                ? `${report.coveredBy} was named to approve this report while you were away`
                                : 'This report was submitted without a regional director stage'}
                            >
                              {report.coveredBy
                                ? `Covered by ${report.coveredBy}`
                                : 'Regional stage removed'} · view only
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {currentReports.length === 0 && !loading && (
            <div className="p-8 text-center">
              <div className="text-4xl mb-2">{activeTab === 'pending' ? '✓' : ''}</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-1">
                {activeTab === 'pending' ? 'All Caught Up'
                  : activeTab === 'sentBack' ? 'Nothing Sent Back' : 'No Approved Reports'}
              </h3>
              <p className="text-gray-500">
                {activeTab === 'pending' ? 'No reports pending your approval.'
                  : activeTab === 'sentBack' ? 'No reports from your region have been sent back.'
                  : 'No approved reports to display.'}
              </p>
            </div>
          )}
        </div>
    </PageShell>
    </>
  );
};

export default RegionalDirectorDashboard;
